import { useState, useEffect } from 'react';
import { helperService } from '../../../services/helperService';
import { realtimeService } from '../../../services/realtimeService';

export const useHelperData = (filters) => {
  const [helpers, setHelpers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadHelpers();

    const subscription = realtimeService?.subscribeToHelpers((payload) => {
      const { eventType, new: newRecord, old: oldRecord } = payload;

      if (eventType === 'INSERT') {
        const newHelper = mapHelperData(newRecord);
        setHelpers((prev) => [newHelper, ...prev]);
      } else if (eventType === 'UPDATE') {
        setHelpers((prev) =>
          prev?.map((helper) =>
            helper?.id === newRecord?.id ? mapHelperData(newRecord) : helper
          )
        );
      } else if (eventType === 'DELETE') {
        setHelpers((prev) => prev?.filter((helper) => helper?.id !== oldRecord?.id));
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [filters]);

  const loadHelpers = async () => {
    setLoading(true);
    setError('');
    
    try {
      const filterParams = {};
      if (filters?.helperType !== 'all') filterParams.type = filters?.helperType;
      if (filters?.expertise !== 'all') filterParams.expertise = filters?.expertise;
      if (filters?.language !== 'all') filterParams.language = filters?.language;
      
      // Map user-friendly availability values to database enum values
      // Only add to filterParams if it's not 'any' or 'all'
      if (filters?.availability && filters?.availability !== 'any' && filters?.availability !== 'all') {
        const availabilityMap = {
          'today': 'available-now',
          'this-week': 'available-soon',
          'next-week': 'available-soon'
        };
        filterParams.availabilityStatus = availabilityMap?.[filters?.availability] || filters?.availability;
      }
      
      const { data, error: fetchError } = await helperService?.getHelpers(filterParams);
      
      if (fetchError) {
        setError(fetchError?.message || 'Failed to load helpers');
        return;
      }
      
      setHelpers(data || []);
    } catch (err) {
      setError(err?.message || 'An error occurred while loading helpers');
    } finally {
      setLoading(false);
    }
  };

  const mapHelperData = (record) => ({
    id: record?.id,
    userId: record?.user_id,
    name: record?.name,
    type: record?.type,
    typeLabel: record?.type_label,
    profileImage: record?.profile_image,
    profileImageAlt: record?.profile_image_alt,
    bio: record?.bio,
    expertise: record?.expertise,
    languages: record?.languages,
    location: record?.location,
    rating: record?.rating,
    reviewCount: record?.review_count,
    sessionsCompleted: record?.sessions_completed,
    availabilityStatus: record?.availability_status,
    verified: record?.verified,
    verificationStatus: record?.verification_status,
    workloadStatus: record?.workload_status,
    sessionsThisMonth: record?.sessions_this_month,
    avgRating: record?.avg_rating,
    credentialExpiry: record?.credential_expiry,
    lastActive: record?.last_active
  });

  return { helpers, loading, error, loadHelpers };
};

export const useHelperSearch = (helpers, searchQuery) => {
  return helpers?.filter(helper => {
    const matchesSearch = helper?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         helper?.bio?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         helper?.expertise?.some(exp => exp?.toLowerCase()?.includes(searchQuery?.toLowerCase()));
    return matchesSearch;
  });
};